import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
    Box,
    Checkbox,
    Chip,
    InputLabel,
    ListItemText,
    MenuItem,
    OutlinedInput,
    Select,
    Typography,
} from "@mui/material";
import { IUserHMIsAccess } from "./UserHMIsAccessInterfaces";
import { useStyles } from "./UserHMIsAccess.styles";
import { IUserMachineCentersByProductionLine } from "../../AddOrEditUserInterfaces";

const itemHeight = 48;
const itemPaddingTop = 8;
const menuProps = {
    PaperProps: {
        style: {
            maxHeight: itemHeight * 4.5 + itemPaddingTop,
            width: 250,
        },
    },
};

const UserHMIsAccess = ({
    productionLines,
    values,
    setFieldValue,
    user,
    disabled,
    clusterAllowed,
}: IUserHMIsAccess) => {
    const classes = useStyles();
    const { t } = useTranslation();
    useEffect(() => {
        if (user && user.user_machine_centers) {
            const parsedUserMachineCenters = user.user_machine_centers.reduce((stack, current) => {
                if (!stack[current.machine_center.production_line_id!]) {
                    stack[current.machine_center.production_line_id!] = [current.machine_center.machine_center_id];
                } else {
                    stack[current.machine_center.production_line_id!].push(current.machine_center.machine_center_id);
                }
                return stack;
            }, {} as IUserMachineCentersByProductionLine);
            setFieldValue("userMachineCenters", parsedUserMachineCenters);
        }
    }, [setFieldValue, user]);

    return (
        <div className={classes.selectHMI}>
            <span className={classes.selectHMITitle}>
                {t("locations.user_tab.add_user.select_machine_hmi_access")}:
            </span>
            <div className={classes.selectHMIContent}>
                {productionLines
                    .filter((productionLine) =>
                        clusterAllowed ? productionLine.machine_centers.some((mc) => mc.cluster === null) : true,
                    )
                    .map((productionLine) => (
                        <Box className={classes.boxContainer} key={productionLine.production_line_id}>
                            <InputLabel id="outlined-user-machine-center-label">
                                <Typography variant="subtitle2" className={classes.formLabel}>
                                    {productionLine.name}
                                </Typography>
                            </InputLabel>
                            <Select
                                fullWidth
                                disabled={disabled}
                                className={classes.select}
                                labelId="user-machine-center-label"
                                id="user-machine-center-chip"
                                multiple
                                value={values.userMachineCenters[productionLine.production_line_id] || []}
                                onChange={(e) => {
                                    setFieldValue(
                                        `userMachineCenters.${productionLine.production_line_id}`,
                                        e.target.value,
                                    );
                                }}
                                input={<OutlinedInput id="user-machine-center-select" />}
                                renderValue={(selected) => (
                                    <Box className={classes.box}>
                                        {selected.map((value) => {
                                            const nonClusterMachineCenter = productionLine.machine_centers.find(
                                                (hmi) => hmi.machine_center_id === value && !hmi.cluster,
                                            );
                                            return (
                                                nonClusterMachineCenter && (
                                                    <Chip
                                                        key={nonClusterMachineCenter.machine_center_id}
                                                        label={nonClusterMachineCenter.name}
                                                    />
                                                )
                                            );
                                        })}
                                    </Box>
                                )}
                                MenuProps={menuProps}
                            >
                                {productionLine.machine_centers
                                    .filter((hmi) => (clusterAllowed ? !hmi.cluster : true))
                                    .map((hmi) => (
                                        <MenuItem
                                            key={hmi.machine_center_id}
                                            value={hmi.machine_center_id}
                                            className={classes.menuItem}
                                        >
                                            <Checkbox
                                                color="info"
                                                checked={
                                                    values.userMachineCenters[productionLine.production_line_id]
                                                        ? values.userMachineCenters[
                                                              productionLine.production_line_id
                                                          ].some((element) => element === hmi.machine_center_id)
                                                        : false
                                                }
                                            />{" "}
                                            <ListItemText>{hmi.name}</ListItemText>
                                        </MenuItem>
                                    ))}
                            </Select>
                        </Box>
                    ))}
            </div>
        </div>
    );
};

export default UserHMIsAccess;
